/**
 * Demand Calculation Rules Editor
 * 
 * Component for editing process demand calculation rules
 */

import React from 'react';
import { Card, Select, InputNumber, Input, Space, Button, Form, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { DemandCalculationRules, DemandCalculationRule } from '../../types/processParameters';
import './DemandCalculationRulesEditor.css';

const { TextArea } = Input;

interface DemandCalculationRulesEditorProps {
  rules: DemandCalculationRules;
  onChange: (rules: DemandCalculationRules) => void;
}

export const DemandCalculationRulesEditor: React.FC<DemandCalculationRulesEditorProps> = ({
  rules,
  onChange
}) => {
  const handlePrimaryRuleChange = (field: keyof DemandCalculationRule, value: any) => {
    onChange({
      ...rules,
      primary_rule: {
        ...rules.primary_rule,
        [field]: value
      } as DemandCalculationRule
    });
  };

  const handleDefaultBehaviorChange = (value: 'PULL' | 'PUSH' | 'NONE') => {
    onChange({
      ...rules,
      default_behavior: value
    });
  };

  const primaryRule = rules.primary_rule;

  return (
    <div className="demand-calculation-rules-editor">
      <Card size="small" title="Primary Demand Rule" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Rule Type */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Rule Type:</label>
            <Select
              style={{ width: '100%' }}
              value={primaryRule?.rule_type || 'MULTIPLIER'}
              onChange={(value) => handlePrimaryRuleChange('rule_type', value)}
            >
              <Select.Option value="FIXED">Fixed Value</Select.Option>
              <Select.Option value="MULTIPLIER">Multiplier</Select.Option>
              <Select.Option value="FORMULA">Formula</Select.Option>
              <Select.Option value="LOOKUP">Lookup Table</Select.Option>
              <Select.Option value="CONSTRAINT">Constraint</Select.Option>
            </Select>
            <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
              {getRuleTypeDescription(primaryRule?.rule_type || 'MULTIPLIER')}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Description:</label>
            <TextArea
              rows={2}
              value={primaryRule?.description || ''}
              onChange={(e) => handlePrimaryRuleChange('description', e.target.value)}
              placeholder="Describe how demand is calculated..."
            />
          </div>

          {/* Rule-specific fields */}
          {primaryRule?.rule_type === 'FIXED' && (
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Fixed Value:</label>
              <InputNumber
                style={{ width: '100%' }}
                value={primaryRule.fixed_value}
                onChange={(value) => handlePrimaryRuleChange('fixed_value', value)}
                placeholder="Enter fixed demand value"
              />
            </div>
          )}

          {primaryRule?.rule_type === 'MULTIPLIER' && (
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Multiplier:</label>
              <InputNumber
                style={{ width: '100%' }}
                value={primaryRule.multiplier}
                onChange={(value) => handlePrimaryRuleChange('multiplier', value)}
                placeholder="e.g., 1.0 for 1:1, 2.0 for double"
                step={0.1}
                min={0}
              />
              <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
                Output demand = Input demand × Multiplier
              </div>
            </div>
          )}

          {primaryRule?.rule_type === 'FORMULA' && (
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Formula:</label>
              <TextArea
                rows={3}
                value={primaryRule.formula}
                onChange={(e) => handlePrimaryRuleChange('formula', e.target.value)}
                placeholder="e.g., input * efficiency_rate * (1 - defect_rate)"
              />
              <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
                Use 'input' for input demand, reference other parameters by name
              </div>
            </div>
          )}

          {primaryRule?.rule_type === 'CONSTRAINT' && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Constraint Type:</label>
                <Select
                  style={{ width: '100%' }}
                  value={primaryRule.constraint_type || 'RANGE'}
                  onChange={(value) => handlePrimaryRuleChange('constraint_type', value)}
                >
                  <Select.Option value="MIN">Minimum</Select.Option>
                  <Select.Option value="MAX">Maximum</Select.Option>
                  <Select.Option value="RANGE">Range</Select.Option>
                </Select>
              </div>

              {(primaryRule.constraint_type === 'MIN' || primaryRule.constraint_type === 'RANGE') && (
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Minimum Value:</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={primaryRule.min_value}
                    onChange={(value) => handlePrimaryRuleChange('min_value', value)}
                    placeholder="Minimum demand"
                  />
                </div>
              )}

              {(primaryRule.constraint_type === 'MAX' || primaryRule.constraint_type === 'RANGE') && (
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Maximum Value:</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={primaryRule.max_value}
                    onChange={(value) => handlePrimaryRuleChange('max_value', value)}
                    placeholder="Maximum demand"
                  />
                </div>
              )}
            </>
          )}

          {/* Priority */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Priority:</label>
            <InputNumber
              style={{ width: 200 }}
              value={primaryRule?.priority || 1}
              onChange={(value) => handlePrimaryRuleChange('priority', value)}
              min={1}
              max={100}
            />
            <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
              Higher priority rules are evaluated first (1 = highest)
            </div>
          </div>
        </Space>
      </Card>

      {/* Default Behavior */}
      <Card size="small" title="Default Behavior">
        <Space direction="vertical" style={{ width: '100%' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Default Demand Flow (when no rules apply):
          </label>
          <Select
            style={{ width: '100%' }}
            value={rules.default_behavior || 'PULL'}
            onChange={handleDefaultBehaviorChange}
          >
            <Select.Option value="PULL">Pull (default)</Select.Option>
            <Select.Option value="PUSH">Push</Select.Option>
            <Select.Option value="NONE">None</Select.Option>
          </Select>
          <div style={{ fontSize: '11px', color: '#888' }}>
            Determines how demand flows if calculation rules don't apply
          </div>
        </Space>
      </Card>

      <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f9ff', borderRadius: 4, fontSize: '12px', color: '#0369a1' }}>
        ℹ️ <strong>Note:</strong> Demand calculation rules determine how demand flows through this process in the projection engine.
        For most processes, a simple MULTIPLIER is sufficient (e.g., 1.0 for 1:1, 2.0 if process doubles demand).
      </div>
    </div>
  );
};

// Helper function for rule type descriptions
function getRuleTypeDescription(ruleType: string): string {
  const descriptions: Record<string, string> = {
    'FIXED': 'Process has a fixed demand regardless of input',
    'MULTIPLIER': 'Multiply input demand by a factor (most common)',
    'FORMULA': 'Calculate demand using a custom formula',
    'LOOKUP': 'Look up demand from a table based on conditions',
    'CONSTRAINT': 'Limit demand to min/max constraints'
  };
  return descriptions[ruleType] || '';
}

